/**
 * Analytics Page
 * Main analytics dashboard with drop-off analysis and real-time updates
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  AlertCircle,
  Filter,
  RefreshCw,
  Download,
} from 'lucide-react';
import { api } from '../../services/api';
import { useDataSourceStore } from '@store/dataSourceStore';
import { useWebSocket } from '@hooks/useWebSocket';
import DateRangeFilter, { DateRange } from '@components/analytics/DateRangeFilter';
import toast from 'react-hot-toast';

interface DropOffData {
  page: string;
  totalViews: number;
  exits: number;
  exitRate: number;
}

const AnalyticsPage = () => {
  // Get selected company and data source from store
  const { selectedCompany, selectedDataSources, getSelection } = useDataSourceStore();

  // Filter to only use data sources that support 'analytics' module
  const analyticsDataSources = selectedDataSources.filter(ds =>
    (ds.availableModules || []).includes('analytics')
  );
  const selectedDataSource = analyticsDataSources[0] || null;

  const { companyId } = getSelection();
  const dataSourceId = selectedDataSource?.id || null;

  // WebSocket for real-time updates
  const { socket, connected } = useWebSocket({ autoConnect: true });

  // Date range state (default: Last 90 days for more data)
  const getDefaultDateRange = (): DateRange => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 90); // Increased to 90 days
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label: 'Last 90 days',
    };
  };

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [deviceFilter, setDeviceFilter] = useState<string>('all');
  const [utmSourceFilter, setUtmSourceFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch drop-off analysis using React Query
  const {
    data: dropOffResponse,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      'drop-off-analysis',
      companyId,
      dataSourceId,
      dateRange.startDate,
      dateRange.endDate,
      deviceFilter,
      utmSourceFilter,
    ],
    queryFn: async () => {
      if (!companyId || !dataSourceId) {
        throw new Error('Please select a company and data source');
      }

      const params: any = {
        companyId,
        dataSourceId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };

      if (deviceFilter !== 'all') params.deviceType = deviceFilter;
      if (utmSourceFilter !== 'all') params.utmSource = utmSourceFilter;

      console.log('🔍 Fetching drop-off analysis with params:', params);

      const response = await api.get('/api/v1/analytics/funnel/drop-off', { params });

      console.log('✅ Drop-off analysis response:', response.data);

      return response.data;
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
  });

  const dropOffData = dropOffResponse?.data?.dropOffAnalysis?.slice(0, 20) || [];
  const error = queryError ? (queryError as any).message : null;

  // WebSocket real-time updates
  useEffect(() => {
    if (!socket || !connected || !companyId) return;

    socket.emit('analytics:subscribe', {
      companyId,
      dataSourceId,
      room: 'analytics-updates',
    });

    socket.on('analytics:drop-off-update', (data: any) => {
      console.log('📊 Real-time drop-off update:', data);
      // Refetch data when updates come in
      refetch();
    });

    return () => {
      socket.off('analytics:drop-off-update');
      socket.emit('analytics:unsubscribe', { companyId, room: 'analytics-updates' });
    };
  }, [socket, connected, companyId, dataSourceId, refetch]);

  const handleRefresh = async () => {
    await refetch();
    toast.success('Analytics refreshed!');
  };

  const handleExport = () => {
    // Export to CSV
    const csv = [
      ['Page', 'Total Views', 'Exits', 'Exit Rate (%)'],
      ...dropOffData.map(d => [d.page, d.totalViews, d.exits, d.exitRate.toFixed(2)]),
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `drop-off-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported to CSV!');
  };

  // Check if user needs to select company/data source
  const needsSelection = !companyId || !dataSourceId;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* No Selection Warning */}
      {needsSelection && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
              Please select a company and data source
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              Use the search bar at the top to select a company and data source to view analytics.
            </p>
          </div>
        </div>
      )}

      {/* Header with Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-secondary-900 dark:text-secondary-50 truncate">
            Analytics Overview
          </h1>
          <p className="text-xs sm:text-sm text-secondary-600 dark:text-secondary-400 mt-1 truncate">
            {selectedCompany?.name} • {selectedDataSource?.name}
            {connected && (
              <span className="ml-2 inline-flex items-center gap-1 text-green-600">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Live
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-secondary flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 py-2"
          >
            <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span className="hidden xs:inline">Filters</span>
          </button>
          <button
            onClick={handleExport}
            className="btn btn-secondary flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 py-2"
          >
            <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span className="hidden xs:inline">Export</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="btn btn-secondary flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 py-2"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 ${loading ? 'animate-spin' : ''}`}
            />
            <span className="hidden xs:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card animate-slide-down">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
            Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Device Type</label>
              <select
                value={deviceFilter}
                onChange={e => setDeviceFilter(e.target.value)}
                className="input"
              >
                <option value="all">All Devices</option>
                <option value="desktop">Desktop</option>
                <option value="mobile">Mobile</option>
                <option value="tablet">Tablet</option>
              </select>
            </div>
            <div>
              <label className="label">UTM Source</label>
              <select
                value={utmSourceFilter}
                onChange={e => setUtmSourceFilter(e.target.value)}
                className="input"
              >
                <option value="all">All Sources</option>
                <option value="google">Google</option>
                <option value="facebook">Facebook</option>
                <option value="direct">Direct</option>
                <option value="organic">Organic</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-red-900 dark:text-red-100">Error Loading Data</p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
          </div>
          <button onClick={() => refetch()} className="btn btn-secondary text-sm">
            Retry
          </button>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="card card-hover">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-secondary-600 dark:text-secondary-400">
                Pages Analyzed
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-16 sm:w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {dropOffData.length}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-secondary-600 dark:text-secondary-400">
                Avg Exit Rate
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-16 sm:w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {dropOffData.length > 0
                    ? (
                        dropOffData.reduce((sum, page) => sum + page.exitRate, 0) /
                        dropOffData.length
                      ).toFixed(1)
                    : '0'}
                  %
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover sm:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-secondary-600 dark:text-secondary-400">
                High Exit Pages
              </p>
              {loading ? (
                <div className="h-6 sm:h-8 w-16 sm:w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {dropOffData.filter(page => page.exitRate > 70).length}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Drop-off Analysis Table */}
      <div className="card overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-secondary-900 dark:text-secondary-50">
            Top Pages by Exit Rate
          </h2>
          <div className="badge badge-info self-start sm:self-auto">{dateRange.label}</div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className="h-12 sm:h-16 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded-lg"
              />
            ))}
          </div>
        ) : dropOffData.length > 0 ? (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-3">
              {dropOffData.map((page, index) => (
                <div
                  key={index}
                  className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-3 border border-secondary-200 dark:border-secondary-700"
                >
                  <div className="font-medium text-sm text-secondary-900 dark:text-secondary-50 mb-2 truncate">
                    {page.page}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-secondary-500 dark:text-secondary-400">Views:</span>
                      <span className="ml-1 font-semibold">{page.totalViews.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-secondary-500 dark:text-secondary-400">Exits:</span>
                      <span className="ml-1 font-semibold">{page.exits.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {page.exitRate > 70 ? (
                        <TrendingDown className="w-3.5 h-3.5 text-red-600" />
                      ) : page.exitRate > 40 ? (
                        <Activity className="w-3.5 h-3.5 text-yellow-600" />
                      ) : (
                        <TrendingUp className="w-3.5 h-3.5 text-green-600" />
                      )}
                      <span className="font-bold">{page.exitRate.toFixed(1)}%</span>
                    </div>
                    <div>
                      {page.exitRate > 70 ? (
                        <span className="badge badge-danger text-xs px-2 py-0.5">Critical</span>
                      ) : page.exitRate > 40 ? (
                        <span className="badge badge-warning text-xs px-2 py-0.5">Medium</span>
                      ) : (
                        <span className="badge badge-success text-xs px-2 py-0.5">Good</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto -mx-6 px-6">
              <table className="table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Total Views</th>
                    <th>Exits</th>
                    <th>Exit Rate</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dropOffData.map((page, index) => (
                    <tr key={index}>
                      <td className="font-medium max-w-md truncate">{page.page}</td>
                      <td>{page.totalViews.toLocaleString()}</td>
                      <td>{page.exits.toLocaleString()}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          {page.exitRate > 70 ? (
                            <TrendingDown className="w-4 h-4 text-red-600" />
                          ) : page.exitRate > 40 ? (
                            <Activity className="w-4 h-4 text-yellow-600" />
                          ) : (
                            <TrendingUp className="w-4 h-4 text-green-600" />
                          )}
                          <span className="font-semibold">{page.exitRate.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td>
                        {page.exitRate > 70 ? (
                          <span className="badge badge-danger">Critical</span>
                        ) : page.exitRate > 40 ? (
                          <span className="badge badge-warning">Medium</span>
                        ) : (
                          <span className="badge badge-success">Good</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-8 sm:py-12 text-secondary-400">
            <Activity className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 opacity-50" />
            <p className="text-base sm:text-lg font-medium">No analytics data available</p>
            <p className="text-xs sm:text-sm mt-2 px-4">
              Data will appear here once tracking events are recorded for the selected period
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
